import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import { useOfferStore } from '@/lib/store';
import { getLineDiscountAmount } from '@/lib/quote-calculations';
import { Position, QuoteItem } from '@shared/schema';
import { GAEBEntry } from '../../../shared/types';
import { ArrowDown, ArrowUp, Calculator, FileText, ListOrdered, Percent, Tag, Plus, GripVertical, Trash2, Sparkles } from 'lucide-react';
import GAEBEditor from './gaeb/GAEBEditor';
import { TextFieldWithModal } from './ui/text-edit-modal';
import PositionAiTextDialog, { type PositionAiTextTarget } from './ui/position-ai-text-dialog';
import { buildPositionAiTextUpdate, type PositionAiTextField } from '@shared/position-ai-text';
import { PositionKalkulationModal } from './kalkulation/position-kalkulation-modal';
import { hatKalkulation } from '@shared/kalkulation';
import QuotePositionImportDialog from './quote-position-import-dialog';
import { herkunftsLabel } from '@/lib/quote-position-copy';

// datalist-IDs dürfen keine Leerzeichen/Sonderzeichen enthalten (invalides HTML).
function untertitelDatalistId(gewerk: string | null | undefined): string {
  const key = (gewerk || 'allgemein').toLowerCase().replace(/[^a-z0-9äöüß-]+/gi, '_');
  return `untertitel-vorschlaege-${key}`;
}

function resolveRowClasses(hasHighlight: boolean, quantity: number, index: number): string {
  if (hasHighlight) {
    return 'bg-green-50/50 border-l-2 border-l-green-400';
  }

  if (quantity > 0) {
    return 'bg-orange-50/70 border-l-2 border-l-orange-300';
  }

  return index % 2 === 0 ? 'bg-white' : 'bg-gray-25';
}

export default function SimpleTable() {
  // Individual selectors to prevent useSyncExternalStore tearing loop
  const positions = useOfferStore(s => s.positions);
  const searchTerm = useOfferStore(s => s.searchTerm);
  const nameSearchTerm = useOfferStore(s => s.nameSearchTerm);
  const sortField = useOfferStore(s => s.sortField);
  const sortDirection = useOfferStore(s => s.sortDirection);
  const isGAEBDatabase = useOfferStore(s => s.isGAEBDatabase);
  const gaebMetadata = useOfferStore(s => s.gaebMetadata);
  const updateGAEBEntry = useOfferStore(s => s.updateGAEBEntry);
  const isDiscountAppliedToPrices = useOfferStore(s => s.isDiscountAppliedToPrices);
  const discountAmount = useOfferStore(s => s.discountAmount);
  const originalPrices = useOfferStore(s => s.originalPrices);
  const quoteHeader = useOfferStore(s => s.quoteHeader);

  const updateQuantity = useOfferStore((state) => state.updateQuantity);
  const updatePosition = useOfferStore((state) => state.updatePosition);
  const setQuoteHeader = useOfferStore((state) => state.setQuoteHeader);
  const setPositions = useOfferStore((state) => state.setPositions);
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const setQuoteItems = useOfferStore((state) => state.setQuoteItems);
  const setSort = useOfferStore((state) => state.setSort);
  const reorderPositions = useOfferStore((state) => state.reorderPositions);
  const removePosition = useOfferStore((state) => state.removePosition);
  const updateItemDiscount = useOfferStore((state) => state.updateItemDiscount);
  const clearItemDiscount = useOfferStore((state) => state.clearItemDiscount);
  const setItemPositionType = useOfferStore((state) => state.setItemPositionType);
  const appendPosition = useOfferStore((state) => state.appendPosition);
  const renumberPositions = useOfferStore((state) => state.renumberPositions);
  const numberingMode = useOfferStore((state) => state.numberingMode);

  const [draggedPositionId, setDraggedPositionId] = useState<number | null>(null);
  const [kalkulationPositionId, setKalkulationPositionId] = useState<number | null>(null);
  // KI-Formulierungshilfe (Wunsch Malermeister): eine Position auf Zuruf,
  // bewusst KEIN Stapellauf über alle Positionen.
  const [aiPositionId, setAiPositionId] = useState<number | null>(null);

  const getReadableId = (position: Position): string => {
    if ('positionNumber' in position && position.positionNumber) {
      return position.positionNumber as string;
    }

    const gewerk = position.gewerk || 'Allgemein';
    const gewerkPositions = positions.filter((p) => p.gewerk === gewerk);
    const gewerkIndex = gewerkPositions.findIndex((p) => p.id === position.id);
    const gewerkNumber = Array.from(new Set(positions.map((p) => p.gewerk || 'Allgemein'))).indexOf(gewerk) + 1;
    return `${gewerkNumber}.${gewerkIndex + 1}`;
  };

  const addNewRow = () => {
    const newId = Math.max(...positions.map((p) => p.id), 0) + 1;
    const lastPosition = positions[positions.length - 1];

    const newPosition: Position = {
      id: newId,
      originalId: String(newId),
      gewerk: lastPosition?.gewerk || 'Neues Gewerk',
      // Neue Zeile übernimmt den Untertitel der letzten Zeile (bleibt in der Gruppe).
      untergewerkName: (lastPosition as any)?.untergewerkName || undefined,
      name: 'Neue Position',
      beschreibung: '',
      einheit: 'Stk',
      preis: 0,
      umsatzsteuer: quoteHeader.mwstSatz ?? 19,
    };

    const newQuoteItem: QuoteItem = {
      id: newId,
      gewerk: newPosition.gewerk || undefined,
      untergewerkName: (newPosition as any).untergewerkName || undefined,
      name: newPosition.name || undefined,
      beschreibung: newPosition.beschreibung,
      einheit: newPosition.einheit,
      preis: Number(newPosition.preis),
      umsatzsteuer: newPosition.umsatzsteuer,
      menge: 0,
    };

    // Dedizierter Append-Pfad: erhält gewerkOrder/editHistory/Suche/numberingMode —
    // der Loader-Pfad setPositions würde die Gewerk-Reihenfolge neu aufbauen und eine
    // Nutzer-Sortierung (inkl. OZ) verwerfen. Renumber (onlyIfAuto) passiert darin.
    appendPosition(newPosition, newQuoteItem);
  };

  const updatePrice = (id: number, newPrice: number) => {
    updatePosition(id, { preis: newPrice });
    setQuoteItems(
      quoteItems.map((item) => (
        item.id === id && item.preis !== newPrice
          // Manuelle Preisänderung: der Preisstand-Vermerk "aus Angebot X"
          // würde danach lügen — hier ZUSÄTZLICH löschen, weil dieser
          // Render-Stand von quoteItems die Löschung aus updatePosition
          // sonst wieder überschreiben würde (stale closure).
          ? { ...item, preis: newPrice, positionsHerkunft: undefined }
          : item
      )),
    );
  };

  if (isGAEBDatabase && gaebMetadata) {
    return <GAEBEditor />;
  }

  const filteredPositions = React.useMemo(() => {
    if (!searchTerm && !nameSearchTerm) {
      return positions;
    }

    return positions.filter((pos) => {
      const generalMatch = !searchTerm || (() => {
        const lowerSearch = searchTerm.toLowerCase();
        return (
          (pos.originalId && pos.originalId.toLowerCase().includes(lowerSearch)) ||
          (pos.gewerk && pos.gewerk.toLowerCase().includes(lowerSearch)) ||
          (pos.name && pos.name.toLowerCase().includes(lowerSearch)) ||
          pos.beschreibung.toLowerCase().includes(lowerSearch) ||
          pos.einheit.toLowerCase().includes(lowerSearch) ||
          String(pos.preis).includes(searchTerm)
        );
      })();

      const nameMatch = !nameSearchTerm || (
        pos.name && pos.name.toLowerCase().includes(nameSearchTerm.toLowerCase())
      );

      return generalMatch && nameMatch;
    });
  }, [positions, searchTerm, nameSearchTerm]);

  const sortedPositions = React.useMemo(() => {
    if (!sortField) {
      return filteredPositions;
    }

    return [...filteredPositions].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (sortField === 'preis' || sortField === 'umsatzsteuer' || sortField === 'id') {
        const aNum = typeof aVal === 'string' ? parseFloat(aVal.replace(/[^\d,.-]/g, '').replace(',', '.')) : Number(aVal);
        const bNum = typeof bVal === 'string' ? parseFloat(bVal.replace(/[^\d,.-]/g, '').replace(',', '.')) : Number(bVal);
        const safeA = Number.isNaN(aNum) ? 0 : aNum;
        const safeB = Number.isNaN(bNum) ? 0 : bNum;
        return sortDirection === 'asc' ? safeA - safeB : safeB - safeA;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? String(aVal || '').localeCompare(String(bVal || ''))
        : String(bVal || '').localeCompare(String(aVal || ''));
    });
  }, [filteredPositions, sortField, sortDirection]);

  const displayedPositions = sortedPositions;
  const canReorder = !sortField && !searchTerm && !nameSearchTerm;

  const handleSort = (field: keyof Position) => {
    if (sortField === field) {
      setSort(field, sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSort(field, 'asc');
  };

  const getSortIcon = (field: keyof Position) => {
    if (sortField !== field) {
      return null;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const safeUpdateQuantity = (id: number, quantity: number) => {
    const rounded = Math.round(quantity * 100) / 100;
    updateQuantity(id, Math.max(0, rounded));
  };

  const updateGAEBPrice = (id: number, ourPrice: number | undefined) => {
    if (isGAEBDatabase && updateGAEBEntry) {
      updateGAEBEntry(id, { ourPrice });
    }
  };

  const updateGAEBComment = (id: number, ourComment: string | undefined) => {
    if (isGAEBDatabase && updateGAEBEntry) {
      updateGAEBEntry(id, { ourComment });
    }
  };

  const focusNextPriceInput = (currentId: number) => {
    const priceInputs = Array.from(document.querySelectorAll<HTMLInputElement>('[data-offer-price-input]'));
    const currentIndex = priceInputs.findIndex((input) => Number(input.dataset.offerPriceInput) === currentId);
    if (currentIndex !== -1 && currentIndex < priceInputs.length - 1) {
      const next = priceInputs[currentIndex + 1];
      next.focus();
      next.select();
    }
  };

  const isGAEBEntry = (position: Position): position is Position & GAEBEntry => (
    isGAEBDatabase && 'positionNumber' in position
  );

  // Ziel der KI-Formulierungshilfe: Beschreibung (Standard-Drucktext) und —
  // falls vorhanden bzw. als Dokument-Fassung gewählt — der Langtext, getrennt
  // wählbar. Bezeichnung (Kurztext), Menge, Einheit, Preis und Pos.-Nr. sind
  // strukturell ausgeschlossen (buildPositionAiTextUpdate wirft für alles andere).
  const aiPosition = aiPositionId !== null
    ? positions.find((p) => p.id === aiPositionId) ?? null
    : null;
  const aiTarget: PositionAiTextTarget | null = aiPosition
    ? {
        scopeKey: `position:${aiPosition.id}`,
        dialogTitle: `KI-Formulierungshilfe — ${aiPosition.name || `Position ${getReadableId(aiPosition)}`}`,
        gewerk: aiPosition.gewerk || undefined,
        felder: [
          { key: 'beschreibung', label: 'Beschreibung', text: aiPosition.beschreibung || '' },
          ...((aiPosition.longText || quoteHeader.textFassung === 'langtext')
            ? [{ key: 'longText', label: 'Langtext', text: aiPosition.longText || '' }]
            : []),
        ],
      }
    : null;

  const handleAiApply = (feldKey: string, text: string) => {
    if (aiPositionId === null) return;
    // buildPositionAiTextUpdate erlaubt NUR beschreibung/longText — jeder andere
    // Feldname wirft, bevor irgendetwas in den Store geschrieben wird.
    updatePosition(aiPositionId, buildPositionAiTextUpdate(feldKey as PositionAiTextField, text));
  };

  const handleDragStart = (positionId: number, event: React.DragEvent<HTMLButtonElement>) => {
    if (!canReorder) {
      event.preventDefault();
      return;
    }
    setDraggedPositionId(positionId);
    event.dataTransfer.setData('text/plain', String(positionId));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (targetPositionId: number, event: React.DragEvent<HTMLTableRowElement>) => {
    if (!canReorder) {
      return;
    }
    event.preventDefault();
    const fromTransfer = Number(event.dataTransfer.getData('text/plain'));
    const sourcePositionId = Number.isFinite(fromTransfer) && fromTransfer > 0 ? fromTransfer : draggedPositionId;

    if (sourcePositionId && sourcePositionId !== targetPositionId) {
      reorderPositions(sourcePositionId, targetPositionId);
    }

    setDraggedPositionId(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement>) => {
    if (!canReorder) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  return (
    <div className="w-full max-w-full h-full flex flex-col pt-6">
      {isGAEBDatabase && (
        <div className="mb-4 flex items-center justify-center">
          <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-200">
            <FileText className="h-3 w-3 mr-1" />
            GAEB-Modus: {gaebMetadata?.projectName || 'Unbekanntes Projekt'}
          </Badge>
        </div>
      )}

      {isDiscountAppliedToPrices && discountAmount > 0 && (
        <div className="mb-4 flex items-center justify-center">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg shadow-md flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-white/20 p-1.5 rounded-full">
                <Percent className="h-4 w-4" />
              </div>
              <div>
                <span className="font-semibold">{discountAmount}% Rabatt aktiv</span>
                <span className="text-green-100 text-sm ml-2">
                  auf alle {quoteItems.length} Positionen angewendet
                </span>
              </div>
            </div>
            <div className="h-6 w-px bg-white/30" />
            <div className="flex items-center gap-1.5 text-sm">
              <Tag className="h-3.5 w-3.5" />
              <span>Einzelpreise reduziert</span>
            </div>
          </div>
        </div>
      )}

      {!canReorder && (
        <div className="mb-2 px-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md py-1.5">
          Drag & Drop ist nur ohne aktive Sortierung und Suche verfügbar.
        </div>
      )}

      <div className="relative flex-1 overflow-y-auto">
        <table className="responsive-table border-collapse text-sm">
          <thead className={isGAEBDatabase ? 'bg-orange-100' : 'bg-blue-100'}>
            <tr className={isGAEBDatabase ? 'bg-orange-100 border-b-2 border-orange-300' : 'bg-blue-100 border-b-2 border-blue-300'}>
              <th className="py-2 px-2 w-[40px] text-center">
                <span className="text-xs font-medium text-gray-600">↕</span>
              </th>

              {isGAEBDatabase && (
                <th className="py-2 px-3 w-[80px] text-left">
                  <div className="flex items-center justify-start px-1 py-0.5">
                    <span className="text-xs font-medium text-orange-700">LV-Nr</span>
                  </div>
                </th>
              )}

              <th className="py-2 px-3 w-[100px] text-left">
                <div
                  className={`flex items-center justify-start cursor-pointer transition-colors rounded px-1 py-0.5 ${sortField === 'originalId' ? (isGAEBDatabase ? 'text-orange-700 font-semibold' : 'text-blue-700 font-semibold') : 'text-gray-600'}`}
                  onClick={() => handleSort('originalId')}
                >
                  <span className="text-xs font-medium">Pos.-Nr.</span>
                  {getSortIcon('originalId')}
                </div>
              </th>

              <th className="py-2 px-3 w-[140px] text-left">
                <div
                  className={`flex items-center justify-start cursor-pointer transition-colors rounded px-1 py-0.5 ${sortField === 'name' ? (isGAEBDatabase ? 'text-orange-700 font-semibold' : 'text-blue-700 font-semibold') : 'text-gray-600'}`}
                  onClick={() => handleSort('name')}
                >
                  <span className="text-xs font-medium">Bezeichnung</span>
                  {getSortIcon('name')}
                </div>
              </th>

              <th className="py-2 px-3 w-[108px] text-right">
                <span className="text-xs font-medium text-gray-600">Menge</span>
              </th>

              <th className="py-2 px-3 w-[35%] text-left">
                <div
                  className={`flex items-center justify-start cursor-pointer transition-colors rounded px-1 py-0.5 ${sortField === 'beschreibung' ? (isGAEBDatabase ? 'text-orange-700 font-semibold' : 'text-blue-700 font-semibold') : 'text-gray-600'}`}
                  onClick={() => handleSort('beschreibung')}
                >
                  <span className="text-xs font-medium">Beschreibung</span>
                  {getSortIcon('beschreibung')}
                </div>
              </th>

              <th className="py-2 px-3 w-[95px] text-left">
                <div
                  className={`flex items-center justify-start cursor-pointer transition-colors rounded px-1 py-0.5 ${sortField === 'gewerk' ? (isGAEBDatabase ? 'text-orange-700 font-semibold' : 'text-blue-700 font-semibold') : 'text-gray-600'}`}
                  onClick={() => handleSort('gewerk')}
                >
                  <span className="text-xs font-medium">Gewerk</span>
                  {getSortIcon('gewerk')}
                </div>
              </th>

              <th className="py-2 px-3 w-[70px] text-left">
                <span className="text-xs font-medium text-gray-600">Einheit</span>
              </th>

              {isGAEBDatabase && (
                <th className="py-2 px-3 w-[80px] text-right">
                  <span className="text-xs font-medium text-orange-700">Original</span>
                </th>
              )}

              <th className="py-2 px-3 w-[142px] text-right">
                <div
                  className={`flex items-center justify-end cursor-pointer transition-colors rounded px-1 py-0.5 ${sortField === 'preis' ? (isGAEBDatabase ? 'text-orange-700 font-semibold' : 'text-blue-700 font-semibold') : 'text-gray-600'}`}
                  onClick={() => handleSort('preis')}
                >
                  <span className="text-xs font-medium">{isGAEBDatabase ? 'Unser Preis' : 'Preis/Einheit'}</span>
                  {isDiscountAppliedToPrices && !isGAEBDatabase && (
                    <span className="ml-1 text-xs text-green-600 font-normal">(-{discountAmount}%)</span>
                  )}
                  {getSortIcon('preis')}
                </div>
              </th>

              {!isGAEBDatabase && (
                <th className="py-2 px-3 w-[136px] text-right">
                  <span className="text-xs font-medium text-gray-600">Rabatt</span>
                </th>
              )}

              {!isGAEBDatabase && (
                <th className="py-2 px-3 w-[130px] text-center">
                  <span className="text-xs font-medium text-gray-600">Positionsart</span>
                </th>
              )}

              {isGAEBDatabase && (
                <th className="py-2 px-3 w-[150px] text-left">
                  <span className="text-xs font-medium text-orange-700">Kommentare</span>
                </th>
              )}

              <th className="py-2 px-3 w-[105px] text-right">
                <span className="text-xs font-medium text-gray-600">Gesamt netto</span>
              </th>

              <th className="py-2 px-2 w-[44px] text-center">
                <span className="text-xs font-medium text-gray-600">✕</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {displayedPositions.length > 0 ? (
              displayedPositions.map((position, index) => {
                const id = position.id;
                const quoteItem = quoteItems.find((item) => item.id === id);
                const quantity = quoteItem?.menge || 0;
                const basePrice = Number(position.preis) || 0;
                const hasDistributedDiscount = isDiscountAppliedToPrices && !!quoteItem && !!originalPrices[id];
                const storedOriginalPrice = originalPrices[id] || basePrice;
                const displayPrice = hasDistributedDiscount && quoteItem
                  ? (typeof quoteItem.preis === 'string' ? parseFloat(quoteItem.preis) : quoteItem.preis)
                  : basePrice;

                const baseLineTotal = (Number.isFinite(displayPrice) ? displayPrice : 0) * quantity;
                const lineDiscount = getLineDiscountAmount({
                  ...(quoteItem || {}),
                  preis: displayPrice,
                  menge: quantity,
                } as any);
                const netLineTotal = Math.max(0, baseLineTotal - lineDiscount);
                const rabattTyp = quoteItem?.rabattTyp === 'absolute' ? 'absolute' : 'percent';
                const rabattWert = Number(quoteItem?.rabattWert || 0);
                const hasLineDiscount = lineDiscount > 0;
                const rowHighlight = hasDistributedDiscount || hasLineDiscount;
                // Positionsart (F1): Bedarf/Alternativ zählen nicht in die Hauptsumme.
                // quoteItem ?? position, damit auch menge=0-Zeilen (kein quoteItem) die
                // gespeicherte Positionsart der Position anzeigen.
                const isBedarf = quoteItem?.isBedarfsposition ?? (position as any).isBedarfsposition;
                const isAlternativ = quoteItem?.isAlternativposition ?? (position as any).isAlternativposition;
                const positionType: 'normal' | 'bedarf' | 'alternativ' =
                  isBedarf ? 'bedarf'
                  : isAlternativ ? 'alternativ'
                  : 'normal';
                const isSonderposition = positionType !== 'normal';
                // "Kein Preis erfasst" (Import ohne Preise): Anzeige "–" statt 0,00 € —
                // dieselbe Wahrheit wie PDF/DOCX. Manuelles Eintragen eines Preises
                // hebt das Flag auf (store.updatePosition).
                const preisFehlt = (quoteItem?.preisFehlt ?? (position as any).preisFehlt) === true;
                // Langtext-Feld zeigen, wenn ein Langtext existiert oder die
                // Langtext-Fassung fürs Dokument gewählt ist (Bestand bleibt unverändert).
                const showLangtextField = !isGAEBDatabase
                  && (Boolean((position as any).longText) || quoteHeader.textFassung === 'langtext');

                return (
                  <tr
                    key={id}
                    data-testid={`offer-position-row-${id}`}
                    className={`hover:bg-gray-50 transition-colors ${resolveRowClasses(rowHighlight, quantity, index)} ${isSonderposition ? 'border-l-4 border-l-amber-400' : ''}`}
                    onDragOver={handleDragOver}
                    onDrop={(event) => handleDrop(id, event)}
                    onDoubleClick={(event) => {
                      if (isGAEBDatabase) return;
                      const target = event.target as HTMLElement;
                      // Doppelklick in Eingabefeldern/Buttons soll normal funktionieren
                      if (target.closest('input, textarea, button, select, [role="combobox"]')) return;
                      setKalkulationPositionId(id);
                    }}
                  >
                    <td className="py-1.5 px-2 text-center">
                      <button
                        type="button"
                        draggable={canReorder}
                        onDragStart={(event) => handleDragStart(id, event)}
                        onDragEnd={() => setDraggedPositionId(null)}
                        className={canReorder ? 'inline-flex items-center justify-center h-6 w-6 rounded cursor-grab text-gray-400 hover:text-gray-700 hover:bg-gray-100' : 'inline-flex items-center justify-center h-6 w-6 rounded cursor-not-allowed text-gray-300'}
                        title={canReorder ? 'Position ziehen zum Sortieren' : 'Sortierung/Suche deaktiviert Drag & Drop'}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                    </td>

                    {isGAEBDatabase && isGAEBEntry(position) && (
                      <td className="py-1.5 px-3 w-[80px]">
                        <div className="text-sm font-medium text-orange-700">{position.positionNumber}</div>
                      </td>
                    )}

                    <td className="py-1.5 px-3 w-[100px]">
                      <div className="text-sm font-medium text-blue-600">{getReadableId(position)}</div>
                    </td>

                    <td className="py-1.5 px-3 w-[140px]">
                      <Input
                        value={position.name || ''}
                        onChange={(e) => updatePosition(id, { name: e.target.value })}
                        className="h-7 text-sm border-gray-200 focus:border-blue-400"
                        placeholder="Bezeichnung"
                      />
                    </td>

                    <td className="py-1.5 px-3 w-[108px]">
                      <div className="flex items-center justify-end">
                        <input
                          ref={(input) => {
                            if (input && !input.matches(':focus')) {
                              input.value = quantity === 0 ? '0' : String(quantity);
                            }
                          }}
                          type="text"
                          inputMode="decimal"
                          defaultValue={quantity === 0 ? '0' : String(quantity)}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              e.target.value = '0';
                              safeUpdateQuantity(id, 0);
                              return;
                            }
                            if (value === '.' || value === ',') {
                              return;
                            }
                            const normalized = value.replace(',', '.');
                            if (/^[0-9]*\.?[0-9]*$/.test(normalized)) {
                              const parsed = parseFloat(normalized);
                              if (!Number.isNaN(parsed)) {
                                safeUpdateQuantity(id, parsed);
                              }
                            }
                          }}
                          className="w-full min-w-[84px] text-right pr-1.5 h-6 text-sm tabular-nums border rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        {/* Bauteilbezug: Menge stammt aus einem IFC-Modell. Der Badge lebt
                            am quoteItem — updateQuantity löscht den Vermerk bei Handänderung,
                            damit hier nie eine gelogene Herkunft steht. */}
                        {quoteItem?.mengeHerkunft && (
                          <Badge
                            variant="outline"
                            className="ml-1 px-1 py-0 text-[10px] leading-4 text-teal-700 border-teal-300 cursor-help"
                            title={`Menge aus IFC-Modell: ${quoteItem.mengeHerkunft.datei} · ${quoteItem.mengeHerkunft.elementAnzahl} Elemente · ${quoteItem.mengeHerkunft.mengenart}${quoteItem.mengeHerkunft.elementeOhneWert > 0 ? ` · Achtung: ${quoteItem.mengeHerkunft.elementeOhneWert} Elemente ohne Wert (Menge unvollständig)` : ''}`}
                          >
                            IFC
                          </Badge>
                        )}
                      </div>
                    </td>

                    <td className="py-1.5 px-3 w-[35%] description-cell">
                      <div className="flex items-start gap-1">
                        <div className="flex-1 min-w-0">
                          <TextFieldWithModal
                            value={position.beschreibung || ''}
                            onChange={(value) => updatePosition(id, { beschreibung: value })}
                            placeholder="Beschreibung"
                            minLengthForModal={100}
                            modalTitle={`Beschreibung bearbeiten - ${position.name || 'Position'}`}
                          />
                        </div>
                        {!isGAEBDatabase && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setAiPositionId(id)}
                            className="h-6 w-6 flex-shrink-0 p-0 mt-0.5 text-purple-400 hover:text-purple-700 hover:bg-purple-50"
                            title="KI-Formulierungshilfe für Beschreibung/Langtext (Vorher/Nachher, Sie entscheiden)"
                            aria-label="KI-Formulierungshilfe öffnen"
                            data-testid={`position-ai-button-${id}`}
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      {showLangtextField && (
                        <div className="mt-1">
                          <TextFieldWithModal
                            value={(position as any).longText || ''}
                            onChange={(value) => updatePosition(id, { longText: value })}
                            placeholder="Langtext (optional)"
                            minLengthForModal={100}
                            modalTitle={`Langtext bearbeiten - ${position.name || 'Position'}`}
                          />
                        </div>
                      )}
                    </td>

                    <td className="py-1.5 px-3 w-[95px]">
                      {/* Titel (Ebene 1) = Gewerk; Untertitel (Ebene 2) darunter.
                          Struktur lebt als Attribut auf der Zeile (shared/quote-numbering.ts). */}
                      <Input
                        value={position.gewerk || ''}
                        onChange={(e) => updatePosition(id, { gewerk: e.target.value })}
                        className="h-7 text-sm border-gray-200 focus:border-blue-400"
                        placeholder="Titel/Gewerk"
                      />
                      {!isGAEBDatabase && (
                        <Input
                          value={(position as any).untergewerkName || ''}
                          onChange={(e) => updatePosition(id, { untergewerkName: e.target.value })}
                          className="h-6 mt-1 text-xs border-gray-100 text-gray-600 focus:border-blue-400"
                          placeholder="Untertitel"
                          list={untertitelDatalistId(position.gewerk)}
                          title="Untertitel (Ebene 2) — leer lassen für Position direkt unter dem Titel"
                        />
                      )}
                    </td>

                    <td className="py-1.5 px-3 w-[70px]">
                      <Input
                        value={position.einheit || ''}
                        onChange={(e) => updatePosition(id, { einheit: e.target.value })}
                        className="h-7 w-16 text-sm border-gray-200 focus:border-blue-400"
                        placeholder="Einheit"
                      />
                    </td>

                    {isGAEBDatabase && isGAEBEntry(position) && (
                      <td className="py-1.5 px-3 text-right w-[80px]">
                        <div className="text-sm text-gray-500">
                          {position.originalPrice ? formatCurrency(position.originalPrice) : '-'}
                        </div>
                      </td>
                    )}

                    <td className="py-1.5 px-3 text-right w-[142px]">
                      {isGAEBDatabase && isGAEBEntry(position) && position.type === 'item' ? (
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={position.ourPrice || ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                            updateGAEBPrice(id, value);
                          }}
                          className="h-6 min-w-[90px] text-xs text-right tabular-nums border-orange-200 focus:border-orange-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          placeholder="Preis eingeben"
                        />
                      ) : hasDistributedDiscount ? (
                        <div className="flex flex-col items-end">
                          <span className="text-xs text-gray-400 line-through">{formatCurrency(storedOriginalPrice)}</span>
                          <span className="text-sm font-medium text-green-600">{formatCurrency(displayPrice)}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setKalkulationPositionId(id)}
                            className={`h-6 w-6 flex-shrink-0 p-0 ${hatKalkulation(position.kalkulation) ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-300 hover:text-blue-600 hover:bg-blue-50'}`}
                            title={hatKalkulation(position.kalkulation) ? 'Tiefenkalkulation bearbeiten (Preis ist kalkuliert)' : 'Tiefenkalkulation öffnen'}
                            aria-label="Tiefenkalkulation öffnen"
                          >
                            <Calculator className="h-3.5 w-3.5" />
                          </Button>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            data-offer-price-input={id}
                            value={basePrice || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                              if (!Number.isNaN(value)) {
                                updatePrice(id, value);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                focusNextPriceInput(id);
                              }
                            }}
                            className="h-6 min-w-[84px] text-xs text-right tabular-nums border-blue-200 focus:border-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="Preis"
                          />
                          {/* Preisstand-Vermerk: Position wurde aus einem anderen
                              Angebot übernommen. updatePosition/updatePrice löschen
                              den Vermerk bei manueller Preisänderung — er darf nie
                              über einem handgesetzten Preis stehen (Muster IFC-Badge). */}
                          {position.positionsHerkunft && (
                            <Badge
                              variant="outline"
                              className="px-1 py-0 text-[10px] leading-4 text-indigo-700 border-indigo-300 cursor-help flex-shrink-0 max-w-[80px] truncate"
                              title={`Übernommen ${herkunftsLabel(position.positionsHerkunft)}${position.positionsHerkunft.kunde ? ` (${position.positionsHerkunft.kunde})` : ''} — Preisstand des Quell-Angebots, Aktualität prüfen. Der Vermerk verschwindet bei manueller Preisänderung.`}
                              data-testid={`position-herkunft-badge-${id}`}
                            >
                              {position.positionsHerkunft.angebotNr}
                            </Badge>
                          )}
                        </div>
                      )}
                    </td>

                    {!isGAEBDatabase && (
                      <td className="py-1.5 px-3 w-[136px]">
                        {quantity > 0 ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <Select
                              value={rabattTyp}
                              onValueChange={(value) => updateItemDiscount(id, rabattWert, value as 'percent' | 'absolute')}
                            >
                              <SelectTrigger className="h-7 w-14 text-xs border-gray-200">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percent">%</SelectItem>
                                <SelectItem value="absolute">€</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              min="0"
                              max={rabattTyp === 'percent' ? '100' : undefined}
                              step={rabattTyp === 'percent' ? '0.1' : '0.01'}
                              value={rabattWert || ''}
                              onChange={(e) => {
                                const next = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                if (!Number.isNaN(next)) {
                                  updateItemDiscount(id, next, rabattTyp);
                                }
                              }}
                              className="h-7 w-16 text-xs text-right border-gray-200"
                              placeholder="0"
                            />
                            {hasLineDiscount && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => clearItemDiscount(id)}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
                                title="Positionsrabatt entfernen"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="text-[11px] text-gray-400 text-right">Menge &gt; 0 für Rabatt</div>
                        )}
                      </td>
                    )}

                    {!isGAEBDatabase && (
                      <td className="py-1.5 px-3 w-[130px] text-center">
                        <Select
                          value={positionType}
                          onValueChange={(value) => setItemPositionType(id, value as 'normal' | 'bedarf' | 'alternativ')}
                        >
                          <SelectTrigger
                            className={`h-7 w-full text-xs ${isSonderposition ? 'border-amber-300 bg-amber-50 text-amber-800 font-medium' : 'border-gray-200'}`}
                            title="Bedarfs-/Alternativposition zählt nicht in die Angebotssumme"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="bedarf">Bedarf/Eventual</SelectItem>
                            <SelectItem value="alternativ">Alternativ</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    )}

                    {isGAEBDatabase && isGAEBEntry(position) && position.type === 'item' && (
                      <td className="py-1.5 px-3 w-[150px]">
                        <Input
                          type="text"
                          value={position.ourComment || ''}
                          onChange={(e) => updateGAEBComment(id, e.target.value || undefined)}
                          className="h-6 text-xs border-orange-200 focus:border-orange-400 text-red-600 placeholder-red-300"
                          placeholder="Kommentar..."
                        />
                      </td>
                    )}

                    <td className="py-1.5 px-3 text-right w-[105px]">
                      <div
                        className={`text-sm font-medium ${preisFehlt ? 'text-gray-400' : isSonderposition ? 'text-amber-700' : rowHighlight && quantity > 0 ? 'text-green-600' : 'text-primary'}`}
                        title={preisFehlt ? 'Kein Preis erfasst — Position erscheint im PDF mit "–" und zählt nicht in die Summe' : undefined}
                      >
                        {preisFehlt ? '–' : quantity > 0 ? formatCurrency(netLineTotal) : '-'}
                      </div>
                      {preisFehlt && (
                        <div className="text-[10px] text-gray-400">Preis fehlt</div>
                      )}
                      {quantity > 0 && !isSonderposition && (hasDistributedDiscount || hasLineDiscount) && (
                        <div className="text-xs text-gray-400 line-through">
                          {formatCurrency(baseLineTotal)}
                        </div>
                      )}
                      {isSonderposition && (
                        <div className="text-[10px] text-amber-600">nicht in Summe</div>
                      )}
                    </td>

                    <td className="py-1.5 px-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePosition(id)}
                        className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                        title="Position löschen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={12} className="h-24 text-center">
                  Keine Ergebnisse gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Untertitel-Vorschläge je Gewerk (datalist für die Untertitel-Inputs).
          Nach sanitisierter ID gruppiert — kollidierende Gewerk-Namen teilen sich
          eine Liste (nur Vorschläge, kein Datenverlust). */}
      {!isGAEBDatabase && Array.from(
        positions.reduce((acc, p) => {
          const listId = untertitelDatalistId(p.gewerk);
          const name = ((p as any).untergewerkName || '').trim();
          if (!acc.has(listId)) acc.set(listId, new Set<string>());
          if (name) acc.get(listId)!.add(name);
          return acc;
        }, new Map<string, Set<string>>())
      ).map(([listId, names]) => (
        <datalist key={listId} id={listId}>
          {Array.from(names).map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      ))}

      <div className="mt-4 px-4 py-2 bg-gray-50 border-t flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={addNewRow}
            className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <Plus className="h-4 w-4" />
            Neue Zeile hinzufügen
          </Button>

          {/* Rosinenpicken (Wunsch Malermeister): Positionen aus bereits
              geschriebenen Angeboten in DIESES laufende Angebot kopieren.
              Sitzt neben "Neue Zeile hinzufügen", weil beides dieselbe
              Handlung ist: dem offenen LV Positionen hinzufügen. */}
          {!isGAEBDatabase && <QuotePositionImportDialog />}

          {!isGAEBDatabase && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => renumberPositions()}
              className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
              title={numberingMode === 'auto'
                ? 'Automatische Nummerierung aktiv — Klick nummeriert sofort neu (1.2.1)'
                : 'GAEB-konforme Nummerierung vergeben (1, 1.1, 1.2.1 …). Achtung: überschreibt vorhandene Positionsnummern.'}
            >
              <ListOrdered className="h-4 w-4" />
              {numberingMode === 'auto' ? 'Nummerierung: automatisch' : 'Neu nummerieren (1.2.1)'}
            </Button>
          )}

          {!isGAEBDatabase && (
            <div
              className="flex items-center gap-1.5"
              title="Welcher Positionstext im PDF gedruckt wird: Standard = Beschreibung (wie bisher), Kurztext = Bezeichnung, Langtext = separater Langtext (Fallback: Beschreibung). Eine Einstellung für das ganze Dokument."
            >
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-600">PDF-Text:</span>
              <Select
                value={quoteHeader.textFassung ?? 'standard'}
                onValueChange={(value) =>
                  setQuoteHeader({
                    ...quoteHeader,
                    textFassung: value === 'kurztext' || value === 'langtext' ? value : undefined,
                  })
                }
              >
                <SelectTrigger className="h-8 w-[170px] text-xs border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Beschreibung (Standard)</SelectItem>
                  <SelectItem value="kurztext">Kurztext (Bezeichnung)</SelectItem>
                  <SelectItem value="langtext">Langtext</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {filteredPositions.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {filteredPositions.length} Positionen
          </span>
        )}
      </div>

      <PositionKalkulationModal
        positionId={kalkulationPositionId}
        open={kalkulationPositionId !== null}
        onOpenChange={(open) => {
          if (!open) setKalkulationPositionId(null);
        }}
      />

      {/* EINE Dialog-Instanz für alle Zeilen (nicht 48 Dialoge). Übernahme
          schreibt ausschließlich Textfelder — siehe handleAiApply. */}
      <PositionAiTextDialog
        target={aiTarget}
        onClose={() => setAiPositionId(null)}
        onApply={handleAiApply}
      />
    </div>
  );
}
